import React, {
  useMemo,
  useRef,
  useState,
  useCallback,
  useEffect,
  useContext,
} from "react"
import PropTypes from "prop-types"
import { useNavigate, useSearchParams } from "react-router-dom"
import {
  DataGridPro,
  useGridApiRef,
  gridFilteredSortedRowIdsSelector,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  GridToolbarColumnsButton,
} from "@mui/x-data-grid-pro"
import { Box, Chip, Tooltip, Typography } from "@mui/material"
import ErrorOutlineSharpIcon from "@mui/icons-material/ErrorOutlineSharp"
import i18n from "i18n"

import DataGridLocaleText from "common/DataGridLocaleText"
import { formatToDDMMYYYY } from "utils/dateUtils"
import { getInvoiceEditUrl, compareDates } from "../utils/invoiceUtils"
import ActionsMenu from "./ActionsMenu"
import AuthContext from "context/auth/authContext"
import useHideDisabledColumns from "hooks/useHideDisabledColumns"
import { getCurrency } from "utils/functions"
import { currencyFormatDE } from "lib/transformers"
import {
  getInvoiceTypeDisplay,
  getRectificationStatusDisplay,
  getInvopopStatusDisplay,
  getInvoiceSignedAmount,
} from "./dataGridUtils"

const CustomToolbar = () => (
  <GridToolbarContainer>
    <GridToolbarColumnsButton />
    <GridToolbarFilterButton />
    <GridToolbarDensitySelector />
    <GridToolbarQuickFilter
      quickFilterParser={searchInput =>
        searchInput.split(",").map(value => value.trim())
      }
      quickFilterFormatter={quickFilterValues => quickFilterValues.join(", ")}
    />
  </GridToolbarContainer>
)

const getRowId = row => row.invoiceId || row.id || row.invoiceNumber

const InvoiceDataGrid = ({ invoices, projects, projectId }) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useContext(AuthContext)
  const apiRef = useGridApiRef()
  const rowsRef = useRef([])

  const currencySymbol = getCurrency(user?.["custom:currency"]) ?? "€"

  const invoiceList = useMemo(
    () => (Array.isArray(invoices) ? invoices : invoices ? [invoices] : []),
    [invoices],
  )

  const useInvopop = useMemo(
    () =>
      invoiceList.some(
        inv =>
          inv.invopopData?.invopopStatus &&
          inv.invopopData.invopopStatus !== "not_sent",
      ),
    [invoiceList],
  )

  const defaultFooterTotals = useMemo(() => {
    const countableInvoices = invoiceList.filter(
      inv => !inv.correctedBy && !inv.cancelledAt,
    )
    const totalInvoiced = countableInvoices.reduce(
      (sum, inv) => sum + getInvoiceSignedAmount(inv),
      0,
    )
    const totalPaid = countableInvoices.reduce(
      (sum, inv) =>
        sum + (inv.paymentStatus === "paid" ? getInvoiceSignedAmount(inv) : 0),
      0,
    )
    const totalUnpaid = countableInvoices.reduce(
      (sum, inv) =>
        sum +
        (inv.paymentStatus === "unpaid" ? getInvoiceSignedAmount(inv) : 0),
      0,
    )
    return {
      totalInvoices: countableInvoices.length,
      totalInvoiced,
      totalPaid,
      totalUnpaid,
    }
  }, [invoiceList])

  const [footerTotals, setFooterTotals] = useState(defaultFooterTotals)

  const updateFooterTotals = useCallback(() => {
    const rows = rowsRef.current
    if (!rows.length) {
      setFooterTotals({
        totalInvoices: 0,
        totalInvoiced: 0,
        totalPaid: 0,
        totalUnpaid: 0,
      })
      return
    }
    let visibleRows = rows
    if (apiRef.current) {
      try {
        const visibleIds = gridFilteredSortedRowIdsSelector(apiRef)
        if (Array.isArray(visibleIds) && visibleIds.length >= 0) {
          const idSet = new Set(visibleIds)
          visibleRows = rows.filter(row => idSet.has(getRowId(row)))
        }
      } catch (err) {
        console.warn("Error getting visible rows", err)
      }
    }
    const countableRows = visibleRows.filter(
      row => !row.correctedBy && !row.cancelledAt,
    )
    const totalInvoiced = countableRows.reduce(
      (sum, row) => sum + getInvoiceSignedAmount(row),
      0,
    )
    const totalPaid = countableRows.reduce(
      (sum, row) =>
        sum +
        (row.paymentStatusRaw === "paid" ? getInvoiceSignedAmount(row) : 0),
      0,
    )
    const totalUnpaid = countableRows.reduce(
      (sum, row) =>
        sum +
        (row.paymentStatusRaw === "unpaid" ? getInvoiceSignedAmount(row) : 0),
      0,
    )
    setFooterTotals({
      totalInvoices: countableRows.length,
      totalInvoiced,
      totalPaid,
      totalUnpaid,
    })
  }, [])

  useEffect(() => {
    setFooterTotals(defaultFooterTotals)
  }, [defaultFooterTotals])

  useEffect(() => {
    const t = setTimeout(updateFooterTotals, 0)
    return () => clearTimeout(t)
  }, [invoiceList.length, updateFooterTotals])

  const paymentQuery = searchParams.get("payment")

  const invoicesGridInitialState = useMemo(() => {
    if (paymentQuery === "unpaid") {
      return {
        filter: {
          filterModel: {
            items: [
              {
                id: 1,
                field: "paymentStatus",
                operator: "equals",
                value: i18n.t("invoices.unpaid"),
              },
            ],
          },
        },
      }
    }
    if (paymentQuery === "paid") {
      return {
        filter: {
          filterModel: {
            items: [
              {
                id: 1,
                field: "paymentStatus",
                operator: "equals",
                value: i18n.t("invoices.paid"),
              },
            ],
          },
        },
      }
    }
    return undefined
  }, [paymentQuery])

  const invoicesDataGridKey = useMemo(
    () => `invoices-dg-${projectId ?? "all"}-${paymentQuery || "all"}`,
    [projectId, paymentQuery],
  )

  useHideDisabledColumns()

  const handleCellClick = params => {
    if (params.field === "actions") return

    const url = getInvoiceEditUrl(params.row.invoiceId, projectId)
    if (url) {
      navigate(url)
    }
  }

  const rows = (
    Array.isArray(invoices) ? invoices : invoices ? [invoices] : []
  ).map(row => {
    const contact = row.contact
    const clientName =
      contact && (contact.firstname || contact.lastname)
        ? `${contact.firstname || ""} ${contact.lastname || ""}`.trim()
        : row.client || ""
    const project =
      projects && projects.find(p => p.projectId === row.projectId)

    let paymentStatusLabel = ""
    if (row.paymentStatus === "paid") {
      paymentStatusLabel = i18n.t("invoices.paid")
    } else if (row.paymentStatus === "unpaid") {
      paymentStatusLabel = i18n.t("invoices.unpaid")
    } else if (row.paymentStatus) {
      paymentStatusLabel = row.paymentStatus
    }

    return {
      ...row,
      paymentStatus: paymentStatusLabel,
      paymentStatusRaw: row.paymentStatus,
      client: clientName,
      projectName: project ? project.name : "",
      issueDateFormatted: row.issueDate ? formatToDDMMYYYY(row.issueDate) : "",
      dueDateFormatted: row.dueDate ? formatToDDMMYYYY(row.dueDate) : "",
      invopopStatusLabel:
        getInvopopStatusDisplay(row.invopopData?.invopopStatus, {
          issued: row.issued,
        })?.label ?? "",
    }
  })

  rowsRef.current = rows

  const columns = [
    {
      field: "issueDateFormatted",
      headerName: i18n.t("invoices.issueDate"),
      width: 120,
      sortComparator: (v1, v2, param1, param2) => {
        const row1 = param1.api.getRow(param1.id)
        const row2 = param2.api.getRow(param2.id)
        return compareDates(row1, row2, "issueDate")
      },
    },
    {
      field: "dueDateFormatted",
      headerName: i18n.t("invoices.dueDate"),
      width: 120,
      sortComparator: (v1, v2, param1, param2) => {
        const row1 = param1.api.getRow(param1.id)
        const row2 = param2.api.getRow(param2.id)
        return compareDates(row1, row2, "dueDate")
      },
    },
    {
      field: "invoiceNumber",
      headerName: i18n.t("invoices.invoiceNumber"),
      width: 140,
      align: "center",
      headerAlign: "center",
      valueGetter: (value, row) =>
        row.issued ? value : i18n.t("invoices.draft"),
      renderCell: params =>
        params.row.issued ? (
          params.row.invoiceNumber
        ) : (
          <Chip
            label={i18n.t("invoices.draft")}
            size="small"
            sx={{ width: "90px" }}
          />
        ),
    },
    {
      field: "invoiceType",
      headerName: i18n.t("invoices.type"),
      width: 130,
      align: "center",
      headerAlign: "center",
      type: "singleSelect",
      valueOptions: [
        i18n.t("invoices.typeStandard"),
        i18n.t("invoices.typeCorrective"),
        i18n.t("invoices.typeCorrected"),
        i18n.t("invoices.typeCreditNote"),
        i18n.t("invoices.typeDebitNote"),
        i18n.t("invoices.typeCancelled"),
      ],
      valueGetter: (value, row) => getInvoiceTypeDisplay(row).label,
      renderCell: params => {
        const { label, color } = getInvoiceTypeDisplay(params.row)
        return (
          <Chip
            label={label}
            size="small"
            variant="outlined"
            color={color}
            sx={{ width: "110px" }}
          />
        )
      },
    },
    {
      field: "rectificationStatus",
      headerName: i18n.t("invoices.rectificationStatus"),
      width: 130,
      align: "center",
      headerAlign: "center",
      type: "singleSelect",
      valueOptions: [
        i18n.t("invoices.rectificationStatusNone"),
        i18n.t("invoices.rectificationStatusPartial"),
        i18n.t("invoices.rectificationStatusFull"),
        i18n.t("invoices.rectificationStatusSubstituted"),
      ],
      valueGetter: (value, row) => {
        const display = getRectificationStatusDisplay(row)
        return display
          ? display.label
          : i18n.t("invoices.rectificationStatusNone")
      },
      renderCell: params => {
        const display = getRectificationStatusDisplay(params.row)
        if (!display) return null
        return (
          <Chip
            label={display.label}
            size="small"
            variant="outlined"
            color={display.color}
            sx={{ width: "110px" }}
          />
        )
      },
    },
    {
      field: "projectName",
      headerName: i18n.t("invoices.project"),
      width: 180,
    },
    {
      field: "client",
      headerName: i18n.t("invoices.client"),
      width: 180,
      renderCell: params => {
        const contact = params.row.contact
        if (contact && (contact.firstname || contact.lastname)) {
          return `${contact.firstname || ""} ${contact.lastname || ""}`.trim()
        }
        return params.row.client || ""
      },
    },
    {
      field: "subtotal",
      headerName: i18n.t("invoices.subtotal"),
      width: 120,
      type: "number",
      align: "right",
      headerAlign: "right",
      renderCell: params =>
        currencyFormatDE(Number(params.row.subtotal) || 0, currencySymbol, 2),
    },
    {
      field: "total",
      headerName: i18n.t("invoices.total"),
      width: 120,
      type: "number",
      align: "right",
      headerAlign: "right",
      renderCell: params =>
        currencyFormatDE(Number(params.row.total) || 0, currencySymbol, 2),
    },
    {
      field: "paymentStatus",
      headerName: i18n.t("invoices.payment"),
      width: 100,
      renderCell: params => {
        const status = params.row.paymentStatus
        if (status === i18n.t("invoices.paid")) {
          return (
            <Chip
              label={status}
              color="success"
              size="small"
              sx={{ width: "90px" }}
            />
          )
        }
        if (status === i18n.t("invoices.unpaid")) {
          return <Chip label={status} size="small" sx={{ width: "90px" }} />
        }
        return (
          <Chip
            label={status || i18n.t("invoices.unknown")}
            size="small"
            sx={{ width: "90px" }}
          />
        )
      },
    },
    {
      field: "issued",
      headerName: i18n.t("invoices.status"),
      width: 100,
      renderCell: params => {
        const issued = params.row.issued
        if (issued) {
          return (
            <Chip
              label={i18n.t("invoices.issued")}
              color="success"
              size="small"
              sx={{ width: "90px" }}
            />
          )
        } else if (params.row.oldInvoiceId) {
          return (
            <Tooltip arrow title={i18n.t("invoices.invoiceNeedsReviewShort")}>
              <Chip
                icon={<ErrorOutlineSharpIcon fontSize="small" color="white" />}
                size="small"
                sx={{
                  width: "90px",
                  backgroundColor: "#ff931E",
                  color: "#fff",
                }}
              />
            </Tooltip>
          )
        }
        return (
          <Chip
            label={i18n.t("invoices.draft")}
            size="small"
            sx={{ width: "90px" }}
          />
        )
      },
    },
    ...(useInvopop
      ? [
          {
            field: "invopopStatusLabel",
            headerName: i18n.t("invoices.invopopStatus.column"),
            width: 140,
            align: "center",
            headerAlign: "center",
            type: "singleSelect",
            valueOptions: [
              i18n.t("invoices.invopopStatus.not_sent"),
              i18n.t("invoices.invopopStatus.pending"),
              i18n.t("invoices.invopopStatus.processing"),
              i18n.t("invoices.invopopStatus.sent"),
              i18n.t("invoices.invopopStatus.accepted"),
              i18n.t("invoices.invopopStatus.accepted_with_incidents"),
              i18n.t("invoices.invopopStatus.failing"),
              i18n.t("invoices.invopopStatus.rejected"),
              i18n.t("invoices.invopopStatus.cancelled"),
              i18n.t("invoices.invopopStatus.error"),
            ],
            renderCell: params => {
              const display = getInvopopStatusDisplay(
                params.row.invopopData?.invopopStatus,
                { issued: params.row.issued },
              )
              if (!display) return null
              return (
                <Chip
                  label={display.label}
                  size="small"
                  color={display.color}
                  sx={{ width: "120px" }}
                />
              )
            },
          },
        ]
      : []),
    {
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      hideable: false,
      renderCell: params => (
        <ActionsMenu row={params.row} projectId={projectId} />
      ),
      headerAlign: "center",
      align: "center",
    },
  ]

  const formatAmount = value =>
    currencyFormatDE(Number(value) || 0, currencySymbol, 2)

  return (
    <>
      <div
        data-testid="invoices-datagrid"
        style={{
          width: "100%",
          height: "min(700px, calc(100vh - 270px))",
        }}
      >
        <DataGridPro
          key={invoicesDataGridKey}
          apiRef={apiRef}
          rows={rows}
          columns={columns}
          getRowId={getRowId}
          {...(invoicesGridInitialState
            ? { initialState: invoicesGridInitialState }
            : {})}
          onCellClick={handleCellClick}
          onFilterModelChange={() => setTimeout(updateFooterTotals, 0)}
          onSortModelChange={() => setTimeout(updateFooterTotals, 0)}
          pageSize={10}
          hideFooter
          hideFooterSelectedRowCount
          pinnedColumns={{ right: ["actions"] }}
          slots={{ toolbar: CustomToolbar }}
          localeText={DataGridLocaleText()}
          sx={{
            border: 0,
            "& .MuiDataGrid-toolbarContainer>Button": {
              color: "rgba(0, 0, 0, 0.87)",
            },
            "& .MuiDataGrid-row": {
              cursor: "pointer",
            },
          }}
        />
      </div>
      <Box
        component="footer"
        sx={{
          display: "flex",
          gap: 3,
          alignItems: "center",
          py: 2,
          px: 0,
          borderTop: 1,
          borderColor: "divider",
          flexWrap: "wrap",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          <strong>{i18n.t("invoices.footerTotalInvoices")}:</strong>{" "}
          {footerTotals.totalInvoices}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>{i18n.t("invoices.footerTotalInvoiced")}:</strong>{" "}
          {formatAmount(footerTotals.totalInvoiced)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>{i18n.t("invoices.footerTotalPaid")}:</strong>{" "}
          {formatAmount(footerTotals.totalPaid)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>{i18n.t("invoices.footerTotalUnpaid")}:</strong>{" "}
          {formatAmount(footerTotals.totalUnpaid)}
        </Typography>
      </Box>
    </>
  )
}

InvoiceDataGrid.propTypes = {
  invoices: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  projects: PropTypes.array,
  projectId: PropTypes.string,
}

export default InvoiceDataGrid
